import axios from "axios";
import { HOST } from "../core/keys";
import { useAuthStore } from "@core/store/storeAuth";

const requestAlegriia = axios.create({
  baseURL: HOST,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Helper to decode JWT and get exp
function getTokenExpiration(token: string): number | null {
  try {
    const payload = token.split(".")[1];
    const decoded: { exp?: number } = JSON.parse(atob(payload));
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}

// Helper to check if token is about to expire (e.g., in next 2 min)
function isTokenExpiringSoon(
  token: string,
  thresholdMs = 1 * 60 * 1000,
): boolean {
  const exp = getTokenExpiration(token);
  if (!exp) return false;
  return exp - Date.now() < thresholdMs;
}

// Interceptor para renovar token antes de cada petición
requestAlegriia.interceptors.request.use(async (config) => {
  const authStore = useAuthStore.getState();
  const token = authStore.token;
  if (token && isTokenExpiringSoon(token)) {
    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const { data } = await axios.post(
          `${HOST}/refresh`,
          {},
          { withCredentials: true },
        );
        const newToken = data.token;
        authStore.setToken(newToken);
        requestAlegriia.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        config.headers.Authorization = `Bearer ${newToken}`;
      } catch (e) {
        console.log(e);
        authStore.setToken("");
      } finally {
        isRefreshing = false;
      }
    } else {
      // Espera a que el token se refresque
      await new Promise((resolve) => {
        refreshSubscribers.push((newToken: string) => {
          config.headers.Authorization = `Bearer ${newToken}`;
          resolve(undefined);
        });
      });
    }
  } else if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const fetcherAlegriia = async (
  data:
    | string
    | {
        url: string;
        params?: Record<string, unknown>;
        auth?: string | null;
        headers?: Record<string, unknown>;
        withCredentials?: boolean;
      },
) => {
  const url = typeof data === "string" ? data : data.url;
  const token = typeof data === "string" ? undefined : data.auth;
  const params = typeof data === "string" ? undefined : data.params;
  const headers = typeof data === "string" ? undefined : data.headers;
  return await requestAlegriia
    .get(url, {
      params,
      headers: {
        ...(token && { Authorization: token }),
        ...headers,
      },
    })
    .then((res) => res.data);
};

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

requestAlegriia.interceptors.response.use(
  (response) => response,
  async (err) => {
    const originalRequest = err.config;

    if (err.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshSubscribers.push((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(requestAlegriia(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await requestAlegriia.post(
          "/refresh",
          {},
          {
            withCredentials: true,
          },
        );
        const newAccess = data.token;
        requestAlegriia.defaults.headers.common.Authorization = `Bearer ${newAccess}`;
        onRefreshed(newAccess);

        return requestAlegriia(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().setToken("");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(err);
  },
);

export { requestAlegriia, fetcherAlegriia };
